import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import type { TelephonyProvider } from '@/hooks/useTelephonyProvider';
import { useTelephonyProvider } from '@/hooks/useTelephonyProvider';

import { useGetRCOAuthStatusQuery } from '@/services/api/ringcentralService';

import {
  DialpadCtiProvider,
  useDialpadCti,
} from './DialpadCtiContext';

export type TelephonyStatus = 'disabled' | 'loading' | 'ready' | 'error';

export interface TelephonyCallOptions {
  phoneNumber: string;
  customData?: Record<string, unknown> | string;
}

interface TelephonyCtiContextValue {
  provider: TelephonyProvider;
  providerLabel: string;
  status: TelephonyStatus;
  isEnabled: boolean;
  isAuthenticated: boolean;
  isPanelOpen: boolean;
  errorMessage: string | null;
  setPanelOpen: (open: boolean) => void;
  openPanel: () => void;
  closePanel: () => void;
  initiateCall: (options: TelephonyCallOptions) => boolean;
  hangUpAll: () => boolean;
}

const TelephonyCtiContext = createContext<TelephonyCtiContextValue | null>(null);

/**
 * Inner provider for Dialpad users — bridges DialpadCtiContext to TelephonyCtiContext.
 */
const DialpadTelephonyBridge: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const dialpad = useDialpadCti();

  const value = useMemo<TelephonyCtiContextValue>(
    () => ({
      provider: 'dialpad',
      providerLabel: 'Dialpad',
      status: dialpad.status,
      isEnabled: dialpad.isEnabled,
      isAuthenticated: dialpad.isAuthenticated,
      isPanelOpen: dialpad.isPanelOpen,
      errorMessage: dialpad.errorMessage,
      setPanelOpen: dialpad.setPanelOpen,
      openPanel: dialpad.openPanel,
      closePanel: dialpad.closePanel,
      initiateCall: (options: TelephonyCallOptions) =>
        dialpad.initiateCall({ phoneNumber: options.phoneNumber, customData: options.customData }),
      hangUpAll: dialpad.hangUpAll,
    }),
    [dialpad],
  );

  return (
    <TelephonyCtiContext.Provider value={value}>
      {children}
    </TelephonyCtiContext.Provider>
  );
};

/**
 * Inner provider for RingCentral users.
 * RC users use the RingCentral desktop/web app for calls, so there is no
 * embedded iframe. This provider exposes a panel for matter contacts and SMS
 * templates, but initiateCall is a no-op (calls are made through the RC app).
 */
const RingCentralTelephonyBridge: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: rcOAuthStatus, refetch: refetchOAuthStatus } =
    useGetRCOAuthStatusQuery();
  const isOAuthConnected = rcOAuthStatus?.connected ?? false;

  // Detect OAuth redirect params on any page
  useEffect(() => {
    const rcParam = searchParams.get('rc');
    if (rcParam === 'connected') {
      toast.success('RingCentral connected successfully');
      searchParams.delete('rc');
      searchParams.delete('tab');
      setSearchParams(searchParams, { replace: true });
      refetchOAuthStatus();
    } else if (rcParam === 'error') {
      toast.error(
        'Failed to connect RingCentral. Please try again or contact support.',
      );
      searchParams.delete('rc');
      searchParams.delete('tab');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, refetchOAuthStatus]);

  const isFeatureEnabled = useMemo(() => {
    const raw = (import.meta.env.VITE_APP_ENABLE_RINGCENTRAL || '')
      .toString()
      .trim()
      .toLowerCase();
    return raw === 'true' || raw === '1';
  }, []);

  const openPanel = useCallback(() => setIsPanelOpen(true), []);
  const closePanel = useCallback(() => setIsPanelOpen(false), []);
  const setPanelOpen = useCallback((open: boolean) => setIsPanelOpen(open), []);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const initiateCall = useCallback((_options: TelephonyCallOptions) => {
    // RC calls are initiated through the RingCentral desktop/web app,
    // not through an embedded CTI widget.
    return false;
  }, []);

  const hangUpAll = useCallback(() => false, []);

  const value = useMemo<TelephonyCtiContextValue>(
    () => ({
      provider: 'ringcentral',
      providerLabel: 'RingCentral',
      status: isFeatureEnabled ? 'ready' : 'disabled',
      isEnabled: isFeatureEnabled,
      isAuthenticated: isOAuthConnected,
      isPanelOpen,
      errorMessage: isOAuthConnected
        ? null
        : 'RingCentral account not connected. Open the communications panel to connect.',
      setPanelOpen,
      openPanel,
      closePanel,
      initiateCall,
      hangUpAll,
    }),
    [isFeatureEnabled, isOAuthConnected, isPanelOpen, setPanelOpen, openPanel, closePanel, initiateCall, hangUpAll],
  );

  return (
    <TelephonyCtiContext.Provider value={value}>
      {children}
    </TelephonyCtiContext.Provider>
  );
};

/**
 * Top-level telephony CTI provider.
 * Wraps DialpadCtiProvider for Dialpad users and provides a simplified
 * RC context for RingCentral users.
 */
export const TelephonyCtiProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isRingCentral } = useTelephonyProvider();

  if (isRingCentral) {
    return (
      <RingCentralTelephonyBridge>{children}</RingCentralTelephonyBridge>
    );
  }

  return (
    <DialpadCtiProvider>
      <DialpadTelephonyBridge>{children}</DialpadTelephonyBridge>
    </DialpadCtiProvider>
  );
};

export const useTelephonyCti = (): TelephonyCtiContextValue => {
  const ctx = useContext(TelephonyCtiContext);
  if (!ctx) {
    throw new Error(
      'useTelephonyCti must be used within TelephonyCtiProvider',
    );
  }
  return ctx;
};
